import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { X, Plus, Save } from "lucide-react";

// Normalise any time string to HH:MM (24-hour) for <input type="time">
const to24h = (timeStr) => {
  if (!timeStr) return "";
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)) return timeStr.slice(0, 5);
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return "";
  let [, h, m, period] = match;
  h = parseInt(h, 10);
  if (period.toUpperCase() === "AM") { if (h === 12) h = 0; }
  else { if (h !== 12) h += 12; }
  return `${String(h).padStart(2, "0")}:${m}`;
};

// Convert HH:MM (24-hour) back to "h:MM AM/PM" for storage
const to12h = (timeStr) => {
  if (!timeStr) return "";
  const match = timeStr.match(/^(\d{2}):(\d{2})$/);
  if (!match) return timeStr;
  let h = parseInt(match[1], 10);
  const m = match[2];
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${period}`;
};

const emptyItem = () => ({ id: Date.now(), name: "", time: "10:00", enabled: true });

// Parse items from the raw API entry object or from a plain array
const parseScheduleItems = (scheduleData) => {
  if (Array.isArray(scheduleData) && scheduleData.length > 0) return scheduleData;
  if (scheduleData && typeof scheduleData === "object" && scheduleData.items) {
    try {
      let parsed = typeof scheduleData.items === "string"
        ? JSON.parse(scheduleData.items)
        : scheduleData.items;
      if (typeof parsed === "string") parsed = JSON.parse(parsed);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((item) => ({
          id: item.id ?? Date.now() + Math.random(),
          name: item.item_name ?? item.name ?? "",
          time: item.time ?? "",
          enabled: item.enabled !== false
        }));
      }
    } catch (e) { /* fall through */ }
  }
  return [emptyItem()];
};

const OrderCutoffScheduleModal = ({ isOpen, onClose, day, scheduleData, onSave }) => {
  const [items, setItems] = useState(() => parseScheduleItems(scheduleData));

  useEffect(() => {
    if (isOpen) {
      setItems(parseScheduleItems(scheduleData));
    }
  }, [isOpen, scheduleData, day]);

  const handleAddItem = () => {
    const newItem = {
      id: Date.now(),
      name: "",
      time: "10:00",
      enabled: true
    };
    setItems([...items, newItem]);
  };

  const handleItemChange = (id, field, value) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleToggle = (id) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, enabled: !item.enabled } : item
    ));
  };

  const handleRemoveItem = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  const handleSave = () => {
    if (onSave) {
      onSave(day, items);
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="order-cutoff-modal"
      overlayClassName="order-cutoff-modal-overlay"
      contentLabel={`${day} Schedule`}
    >
      <div className="order-cutoff-modal-content">
        <div className="order-cutoff-modal-header">
          <h2 className="order-cutoff-modal-title">{day}</h2>
          <div className="order-cutoff-modal-header-actions">
            <button className="order-cutoff-modal-save-icon" onClick={handleSave}>
              <Save size={20} />
            </button>
            <button className="order-cutoff-modal-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="order-cutoff-modal-body">
          {items.map((item, index) => (
            <div key={item.id} className="order-cutoff-item-row">
              <input
                type="text"
                className="order-cutoff-item-input"
                value={item.name}
                onChange={(e) => handleItemChange(item.id, "name", e.target.value)}
                placeholder="Item name"
              />
              <input
                type="time"
                className="order-cutoff-time-input"
                value={to24h(item.time)}
                onChange={(e) => handleItemChange(item.id, "time", to12h(e.target.value))}
              />
              <label className="order-cutoff-toggle">
                <input
                  type="checkbox"
                  checked={item.enabled}
                  onChange={() => handleToggle(item.id)}
                  className="order-cutoff-toggle-input"
                />
                <span className="order-cutoff-toggle-slider"></span>
              </label>
              {items.length > 1 && (
                <button
                  className="order-cutoff-remove-btn"
                  onClick={() => handleRemoveItem(item.id)}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ))}

          <button className="order-cutoff-add-btn" onClick={handleAddItem}>
            <Plus size={18} />
            Add another item
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default OrderCutoffScheduleModal;

